// OIDC client config. Vite substitutes `import.meta.env.VITE_*` only at
// LITERAL property access — dynamic `env[key]` does not work, so each var
// is read explicitly below.
//
// In real dev, set:
//   VITE_OIDC_ISSUER=http://localhost:8080            (per-dev local Zitadel)
//   VITE_OIDC_CLIENT_ID=lyse-fe
//   VITE_OIDC_REDIRECT_URI=http://localhost:5173/auth/callback   (optional;
//     defaults to `${window.location.origin}/auth/callback` at runtime)
//
// Go-live 3.1 — every getter consults the container-injected runtime
// config (`window.__AIQ_CONFIG__`, see ./runtime-config.ts) FIRST, then
// the build-time VITE_* value, then the hard default. One image serves
// staging + prod; dev/test behaviour is unchanged (empty runtime config).

import { pickConfigValue, readRuntimeConfig } from "./runtime-config.js";

export interface OidcConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

interface ViteEnv {
  VITE_OIDC_ISSUER?: string;
  VITE_OIDC_CLIENT_ID?: string;
  VITE_OIDC_REDIRECT_URI?: string;
  /** Base URL for the LYSE module's API surface (analytics). */
  VITE_API_BASE_URL?: string;
  /** Base URL for the platform-level AUTH API surface. */
  VITE_AUTH_BASE_URL?: string;
  /** Base URL for the ASSETS API surface (sites, turbines, asset-models). */
  VITE_ASSETS_BASE_URL?: string;
  /** Base URL for the NOTIFICATIONS API surface (SB2-W-15). */
  VITE_NOTIFICATIONS_BASE_URL?: string;
  /** Base URL for the PROJECTS API surface (SB2-W-16). */
  VITE_PROJECTS_BASE_URL?: string;
  /** Base URL for the TAXONOMY API surface (DEFCAT P6 catalogue admin). */
  VITE_TAXONOMY_BASE_URL?: string;
  /** Base URL for the FLEET API surface (MAP-3 fleet-map aggregation). */
  VITE_FLEET_BASE_URL?: string;
  /** Base URL for the DASHBOARD API surface (ONGOING-1 CX aggregates). */
  VITE_DASHBOARD_BASE_URL?: string;
  /** Base URL for the TENANCY API surface (TNCY-5 cross-tenant links). */
  VITE_TENANCY_BASE_URL?: string;
  /** Base URL for the SUPPORT API surface (SUP-ADMIN-UI-01 bug-report triage). */
  VITE_SUPPORT_BASE_URL?: string;
  /** Base URL for the TIME-TRACKING API surface (TT-02 standby reporting). */
  VITE_TIME_TRACKING_BASE_URL?: string;
  /** Base URL for the AFIS API surface (ADM-DEV-01 unit registry). */
  VITE_AFIS_BASE_URL?: string;
  /** Base URL for the INSPECTION-3 API surface (DI-2-3 intake: acquisition create). */
  VITE_INSPECTION3_BASE_URL?: string;
  /** Base URL for the INTAKE API surface (DI-2-4 wiring; first consumer DI-2-9). */
  VITE_INTAKE_BASE_URL?: string;
  /** Base URL for the WORKFLOWS API surface (M3C-04 step 0). */
  VITE_WORKFLOWS_BASE_URL?: string;
  /** MapLibre basemap style URL (MAP-1 self-hosted PMTiles on Cloudflare R2). */
  VITE_MAP_STYLE_URL?: string;
}

function readEnv(): ViteEnv {
  // In Vite-served code, `import.meta.env` is replaced statically. In
  // non-Vite contexts (Node tests), it may be undefined.
  try {
    return (import.meta as unknown as { env?: ViteEnv }).env ?? {};
  } catch {
    return {};
  }
}

export function getOidcConfig(): OidcConfig {
  const env = readEnv();
  const runtime = readRuntimeConfig();
  // For tests + local dev with no env override, default the issuer to the
  // page's own origin. The Playwright suite stubs `**/oidc/v1/*` URLs and
  // its `route.fulfill` 302s use relative `Location` headers — keeping the
  // stubbed Zitadel on the same origin makes those redirects land back in
  // the SPA rather than off-host.
  const sameOriginDefault =
    typeof window !== "undefined"
      ? window.location.origin
      : "http://localhost:5173";
  const issuer =
    pickConfigValue(runtime.oidcIssuer, env.VITE_OIDC_ISSUER) ??
    sameOriginDefault;
  const clientId =
    pickConfigValue(runtime.oidcClientId, env.VITE_OIDC_CLIENT_ID) ?? "lyse-fe";
  const explicitRedirect = pickConfigValue(
    runtime.oidcRedirectUri,
    env.VITE_OIDC_REDIRECT_URI,
  );
  const redirectUri =
    explicitRedirect ??
    (typeof window !== "undefined"
      ? `${window.location.origin}/auth/callback`
      : "http://localhost:5173/auth/callback");
  return {
    issuer,
    clientId,
    redirectUri,
    // Two Zitadel reserved scopes are layered on top of the four standard
    // OIDC scopes:
    //
    //   • `urn:zitadel:iam:user:resourceowner` — emits the
    //     `urn:zitadel:iam:user:resourceowner:id` claim (the user's org id /
    //     tenantId). Required by the BE's tenant-mismatch guard
    //     (`auth.controller.ts::getMe`). Without it `/api/auth/v1/me`
    //     403s with `claims.tenantId=undefined`. Added for FE-BUG-002.
    //
    //   • `urn:zitadel:iam:org:projects:roles` (plural `projects`) — emits
    //     the `urn:zitadel:iam:org:project:roles` map claim, keyed by
    //     project-role name (`{ tenant_admin: { … } }`). Required by
    //     `RoleGuard` for every authenticated request that has a
    //     `@RequireRole` decorator. Without it Zitadel suppresses the map
    //     entirely (even when the OIDC app has
    //     `access_token_role_assertion = true` AND the user has active
    //     `userGrants`) and the BE sees `roles=[]`. Symptom: every
    //     `tenant_admin`-gated route 403s; no Add/Edit buttons render.
    //     Added for SB4-01 / BE-050 (2026-05-22).
    //
    // Reference: https://zitadel.com/docs/apis/openidoauth/scopes
    scope:
      "openid profile email offline_access urn:zitadel:iam:user:resourceowner urn:zitadel:iam:org:projects:roles",
  };
}

export function getApiBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().apiBaseUrl,
      readEnv().VITE_API_BASE_URL,
    ) ?? "/api/lyse/v1"
  );
}

/**
 * Auth API base URL. Distinct from `getApiBaseUrl()` because auth was
 * relocated to its own platform-level namespace at `contracts/auth/v1/`
 * (see .vibe/21_AUTH_NAMESPACE_RELOCATION.md). Used by `useTenantFromHost`,
 * `useMe`, and `useAuth.signOut` to build the three auth endpoint URLs:
 *   GET  ${auth-base}/tenant-from-host
 *   GET  ${auth-base}/me
 *   POST ${auth-base}/logout
 *
 * Override via `VITE_AUTH_BASE_URL`. Defaults to `/api/auth/v1` (proxied
 * by Vite to the api-gateway in dev; nginx-routed in prod).
 */
export function getAuthBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().authBaseUrl,
      readEnv().VITE_AUTH_BASE_URL,
    ) ?? "/api/auth/v1"
  );
}

/**
 * Assets API base URL. Sites, turbines, asset-models all live under
 * the `assets/v1` namespace (locked 2026-05-12). Distinct from
 * `getApiBaseUrl()` (LYSE / analytics) and `getAuthBaseUrl()` (auth).
 *
 * Override via `VITE_ASSETS_BASE_URL`. Defaults to `/api/assets/v1`
 * (proxied by Vite to the api-gateway in dev; nginx-routed in prod).
 */
export function getAssetsBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().assetsBaseUrl,
      readEnv().VITE_ASSETS_BASE_URL,
    ) ?? "/api/assets/v1"
  );
}

/**
 * Notifications API base URL. SB2-W-15 / OQ-SB2-B2(b) — notifications
 * relocated out of `auth/v1` into their own domain. Distinct from
 * `getAuthBaseUrl()` because notifications are a cross-cutting workflow
 * concern, not authentication.
 *
 * Override via `VITE_NOTIFICATIONS_BASE_URL`. Defaults to
 * `/api/notifications/v1` (Vite-proxied in dev; nginx-routed in prod).
 */
export function getNotificationsBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().notificationsBaseUrl,
      readEnv().VITE_NOTIFICATIONS_BASE_URL,
    ) ?? "/api/notifications/v1"
  );
}

/**
 * Projects API base URL. SB2-W-16 — Project Management → Projects page.
 * Projects live in their own contract domain because they're a
 * planning artefact distinct from assets/auth/notifications/lyse.
 *
 * Override via `VITE_PROJECTS_BASE_URL`. Defaults to
 * `/api/projects/v1` (Vite-proxied in dev; nginx-routed in prod).
 */
export function getProjectsBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().projectsBaseUrl,
      readEnv().VITE_PROJECTS_BASE_URL,
    ) ?? "/api/projects/v1"
  );
}

/**
 * Taxonomy API base URL. DEFCAT P6 — the tenant Defect & AI management admin
 * WRITE surface (catalogue editor) lives in its own `taxonomy/v1` contract
 * domain, distinct from the `lyse/v1` analyst consumer surface. The
 * `lyse/v1@7.9.0` `GET /defect-catalogues` list endpoint stays on the LYSE
 * base; only the admin write endpoints route here.
 *
 * Override via `VITE_TAXONOMY_BASE_URL`. Defaults to `/api/taxonomy/v1`
 * (Vite-proxied in dev; nginx-routed in prod).
 */
export function getTaxonomyBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().taxonomyBaseUrl,
      readEnv().VITE_TAXONOMY_BASE_URL,
    ) ?? "/api/taxonomy/v1"
  );
}

/**
 * Fleet API base URL. MAP-3 — the fleet-map geo-aggregation endpoint lives in
 * its own `fleet/v1` contract domain (a fleet-geo concern, not analysis), so it
 * gets its own base distinct from `getApiBaseUrl()` (LYSE).
 *
 * Override via `VITE_FLEET_BASE_URL`. Defaults to `/api/fleet/v1` (Vite-proxied
 * in dev; nginx-routed in prod).
 */
export function getFleetBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().fleetBaseUrl,
      readEnv().VITE_FLEET_BASE_URL,
    ) ?? "/api/fleet/v1"
  );
}

/**
 * Dashboard API base URL. ONGOING-1 — the customer-experience dashboard
 * aggregates (ongoing-projects, future critical-findings) live in their own
 * `dashboard/v1` contract domain (a dashboard-read concern, pre-aggregated
 * server-side), distinct from `getApiBaseUrl()` (LYSE) and `getFleetBaseUrl()`.
 *
 * Override via `VITE_DASHBOARD_BASE_URL`. Defaults to `/api/dashboard/v1`
 * (Vite-proxied in dev; nginx-routed in prod).
 */
export function getDashboardBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().dashboardBaseUrl,
      readEnv().VITE_DASHBOARD_BASE_URL,
    ) ?? "/api/dashboard/v1"
  );
}

/**
 * Tenancy API base URL. TNCY-5 — cross-tenant company visibility
 * (invitations + links) lives in its own `tenancy/v1` contract domain
 * (relationships *between* tenants, distinct from a project's own data in
 * projects/v1). Backs the Administration → Partner Companies page.
 *
 * Override via `VITE_TENANCY_BASE_URL`. Defaults to `/api/tenancy/v1`
 * (Vite-proxied in dev; nginx-routed in prod).
 */
export function getTenancyBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().tenancyBaseUrl,
      readEnv().VITE_TENANCY_BASE_URL,
    ) ?? "/api/tenancy/v1"
  );
}

/**
 * Support API base URL. SUP-ADMIN-UI-01 — the platform-admin bug-report /
 * support-ticket triage surface lives in its own cross-cutting `support/v1`
 * contract domain (ingest + triage of bug reports + their diagnostic-zip
 * attachments), distinct from the read-only `notifications/v1` and the
 * per-client `inspection-3/v1`. The triage read/patch endpoints are
 * platform-admin only and cross-tenant (no `X-Tenant-ID`).
 *
 * Override via `VITE_SUPPORT_BASE_URL`. Defaults to `/api/support/v1`
 * (Vite-proxied in dev; nginx-routed in prod).
 */
export function getSupportBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().supportBaseUrl,
      readEnv().VITE_SUPPORT_BASE_URL,
    ) ?? "/api/support/v1"
  );
}

/**
 * Time-tracking API base URL. TT-02 — standby / time tracking lives in its own
 * cross-cutting `time-tracking/v1` contract domain: it anchors across projects,
 * contracts and instructions and is consumed by three unrelated surfaces (the
 * pilot mobile page, the back-office review table, the project settings card),
 * so it is deliberately not folded into `projects/v1`.
 *
 * Unlike `getSupportBaseUrl()` this surface IS tenant-scoped — every call sends
 * `X-Tenant-ID` (see `timeTrackingFetch.ts`).
 *
 * Override via `VITE_TIME_TRACKING_BASE_URL`. Defaults to
 * `/api/time-tracking/v1` (Vite-proxied in dev; nginx-routed in prod).
 */
export function getTimeTrackingBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().timeTrackingBaseUrl,
      readEnv().VITE_TIME_TRACKING_BASE_URL,
    ) ?? "/api/time-tracking/v1"
  );
}

/**
 * afis/v1 — the flight-intelligence unit surface (ADM-DEV-01/ADM-FW-01).
 *
 * Mostly a DEVICE API the frontend never calls; what the Platform Admin app
 * consumes is the admin half — the device registry and the firmware/preset
 * registries, both behind a human JWT and `afis.manage_devices`.
 */
export function getAfisBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().afisBaseUrl,
      readEnv().VITE_AFIS_BASE_URL,
    ) ?? "/api/afis/v1"
  );
}

/**
 * inspection-3/v1 — the field-client write surface the intake wizard creates
 * its Data Acquisitions on (DI-2-3; DI-D34 c1: the intake extends
 * `inspection-3/v1`). Tenant-scoped: bearer + `X-Tenant-ID`.
 *
 * Override via `VITE_INSPECTION3_BASE_URL` (wired through
 * `tooling/scripts/resolve-dev-env.mjs` and the `vite.config.ts` define block,
 * or realbe mode 500s) or the runtime `inspection3BaseUrl`. Defaults to
 * `/api/inspection-3/v1`.
 */
export function getInspection3BaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().inspection3BaseUrl,
      readEnv().VITE_INSPECTION3_BASE_URL,
    ) ?? "/api/inspection-3/v1"
  );
}

/**
 * intake/v1 — the browser intake's own domain: log candidates, the
 * questionnaire, flights (DI-D34 c2). Tenant-scoped: bearer + `X-Tenant-ID`.
 * Wired by DI-2-4 so DI-2-9 is FE-only work; nothing calls it yet.
 *
 * Override via `VITE_INTAKE_BASE_URL` (wired through
 * `tooling/scripts/resolve-dev-env.mjs` and the `vite.config.ts` define block)
 * or the runtime `intakeBaseUrl`. Defaults to `/api/intake/v1`.
 */
export function getIntakeBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().intakeBaseUrl,
      readEnv().VITE_INTAKE_BASE_URL,
    ) ?? "/api/intake/v1"
  );
}

/**
 * workflows/v1 — the tenant's own active workflows and the injection nodes
 * shared with it (M3C-04 step 0; DI-D62). Tenant-scoped: bearer + `X-Tenant-ID`.
 *
 * Override via `VITE_WORKFLOWS_BASE_URL` (wired through
 * `tooling/scripts/resolve-dev-env.mjs` and the `vite.config.ts` define block)
 * or the runtime `workflowsBaseUrl`. Defaults to `/api/workflows/v1`.
 */
export function getWorkflowsBaseUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().workflowsBaseUrl,
      readEnv().VITE_WORKFLOWS_BASE_URL,
    ) ?? "/api/workflows/v1"
  );
}

/**
 * MapLibre basemap style URL (MAP-1). Resolution order: container runtime config
 * (`window.__AIQ_CONFIG__.mapStyleUrl`, set per-environment via /config.js — no
 * rebuild) → build-time `VITE_MAP_STYLE_URL` → the default below.
 *
 * **Interim default = OpenFreeMap `positron`** — a free, no-API-key, worldwide
 * OSM/OpenMapTiles vector style (muted light-grey so data colours pop). Chosen
 * for testing (Christian, 2026-06-14): zero ops, zero storage, global coverage
 * (the fleet spans 38 countries). Trade-off: tile requests hit a third party
 * (soft location-leak) on a community/best-effort service.
 *
 * **Production / sovereignty target stays self-hosted PMTiles** on
 * `tiles.wind-platform.com` (locked spec §8.1, ~$1.35/mo, zero third-party
 * calls). Flip to it by setting `mapStyleUrl` in /config.js (no rebuild) once
 * the planet `.pmtiles` is uploaded — see _BACKEND/infrastructure/maps.
 */
export function getMapStyleUrl(): string {
  return (
    pickConfigValue(
      readRuntimeConfig().mapStyleUrl,
      readEnv().VITE_MAP_STYLE_URL,
    ) ?? "https://tiles.openfreemap.org/styles/positron"
  );
}

export const SESSION_STORAGE_KEY = "aero.auth";
export const OIDC_PENDING_STORAGE_KEY = "aero.oidc.pending";
